import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  PrinterIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import { publicReservationAPI } from '../../services/api';

function formatMoney(value?: string | number | null) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatDateTime(value?: string | null) {
  if (!value) return 'TBD';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'TBD'
    : date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

function formatDate(value?: string | null) {
  if (!value) return 'TBD';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'TBD'
    : date.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

function labelize(value?: string | null) {
  return (value || 'N/A')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatAddress(value?: Record<string, any> | string | null, fallback = 'TBD') {
  if (typeof value === 'string') return value || fallback;
  if (!value || typeof value !== 'object') return fallback;
  if (value.formatted || value.formatted_location) {
    return String(value.formatted || value.formatted_location);
  }

  return (
    [
      value.name,
      value.line1 || value.address_line1,
      value.line2 || value.address_line2,
      [value.city, value.state, value.postalCode || value.postal_code]
        .filter(Boolean)
        .join(', '),
    ]
      .filter(Boolean)
      .join(' - ') || fallback
  );
}

function getStopAddress(stop: Record<string, any>) {
  return formatAddress(
    stop.address || stop,
    stop.formatted_location || stop.location || 'TBD',
  );
}

function getAddressNote(value?: Record<string, any> | null) {
  if (!value || typeof value !== 'object') return null;
  return value.notes ? String(value.notes) : null;
}

function statusLabel(record: Record<string, any>) {
  const status = String(record.status || '').toLowerCase();
  if (status === 'confirmed' || status === 'active') return 'Reservation confirmed';
  if (status === 'completed') return 'Trip completed';
  if (status === 'cancelled') return 'Reservation cancelled';
  if (status === 'pending') return 'Reservation pending';
  return labelize(record.status || 'Reservation confirmed');
}

function formatDepositPolicy(terms?: Record<string, any> | null) {
  const deposit = terms?.deposit || {};
  if (!deposit.type || deposit.type === 'none') return 'No deposit required';

  const amount =
    deposit.type === 'fixed'
      ? formatMoney(deposit.value)
      : `${Number(deposit.value || 0).toFixed(0)}%`;
  const due =
    deposit.due === 'on_acceptance'
      ? 'due when accepted'
      : deposit.due === 'before_trip'
      ? 'due before service'
      : 'due with invoice';

  return `${amount} deposit ${due}`;
}

function Section({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase text-on-surface-variant">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="mt-1 text-xl font-semibold text-on-surface">{title}</h2>
      {description ? (
        <p className="mt-2 text-sm leading-6 text-on-surface-variant">
          {description}
        </p>
      ) : null}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function SummaryLine({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-on-surface-variant">{label}</dt>
      <dd className="text-right font-medium text-on-surface">{value}</dd>
    </div>
  );
}

export default function PublicReservationRecordPage() {
  const { token } = useParams();
  const {
    data: record,
    isLoading,
    error,
  } = useQuery(
    ['public-reservation-record', token],
    async () => {
      const response = await publicReservationAPI.getReservationRecord(token || '');
      return response.data;
    },
    {
      enabled: Boolean(token),
      retry: false,
    },
  );

  const tripDetails = record?.tripDetails || {};
  const company = record?.company || {};
  const companyName = company.appName || company.name || 'ModalOS';
  const primaryColor = company.primaryColor || '#2563eb';
  const quoteSource = record?.quoteSource || {};
  const billingSummary = record?.billingSummary || {};
  const depositPayment = billingSummary.depositPayment || {};
  const termsSnapshot =
    quoteSource.termsSnapshot || record?.splitBillingConfig?.terms_snapshot || null;
  const vehicleRequirements = useMemo(
    () =>
      Array.isArray(record?.vehicleRequirements)
        ? record.vehicleRequirements
        : [],
    [record?.vehicleRequirements],
  );
  const vehicleAssignments = useMemo(
    () =>
      Array.isArray(record?.vehicleAssignments)
        ? record.vehicleAssignments
        : [],
    [record?.vehicleAssignments],
  );
  const stops = Array.isArray(tripDetails.stops) ? tripDetails.stops : [];
  const pickupAddress = tripDetails.pickup_address;
  const dropoffAddress = tripDetails.dropoff_address;
  const depositPaymentUrl =
    billingSummary.depositPaymentUrl ||
    depositPayment.publicUrl ||
    quoteSource.depositPaymentUrl ||
    tripDetails.deposit_payment_url ||
    null;
  const depositStatus = String(
    billingSummary.depositStatus ||
      (record?.paymentStatus === 'deposit_paid'
        ? 'paid'
        : record?.paymentStatus === 'deposit_due'
        ? 'due'
        : 'not_required')
  ).toLowerCase();
  const showDepositAction =
    depositStatus === 'due' && Boolean(depositPaymentUrl);
  const showDepositRecordAction =
    depositStatus === 'paid' && Boolean(depositPaymentUrl);
  const contractTotal = billingSummary.contractTotal || record?.totalPrice;
  const balanceRemaining =
    billingSummary.balanceRemaining != null
      ? billingSummary.balanceRemaining
      : record?.totalPrice;
  const balanceRemainingAmount = Number(balanceRemaining || 0);
  const balanceStatus = String(billingSummary.balanceStatus || 'not_invoiced').toLowerCase();
  const balancePaymentUrl = billingSummary.balancePaymentUrl || null;
  const showBalanceAction =
    ['ready_to_pay', 'awaiting_payment'].includes(balanceStatus) &&
    Boolean(balancePaymentUrl) &&
    Number.isFinite(balanceRemainingAmount) &&
    balanceRemainingAmount > 0;
  const showBalanceReceived =
    balanceStatus === 'paid' ||
    (Number.isFinite(balanceRemainingAmount) && balanceRemainingAmount <= 0);
  const balanceStatusLabel = showBalanceReceived
    ? 'Received'
    : showBalanceAction
    ? 'Payment available'
    : balanceStatus === 'draft'
    ? 'Invoice being prepared'
    : balanceStatus === 'not_invoiced'
    ? 'Not yet invoiced'
    : labelize(balanceStatus);
  const balanceSummaryNote = showBalanceReceived
    ? 'Your balance has been received.'
    : showBalanceAction
    ? 'Your remaining balance is ready for secure payment.'
    : 'Your remaining balance has not been opened for payment yet.';
  const depositPaidAmount = billingSummary.depositPaid || '0.00';
  const depositAmount = Number(
    billingSummary.depositRequired || billingSummary.depositAmount || record?.depositAmount || 0
  );
  const hasDeposit = Number.isFinite(depositAmount) && depositAmount > 0;
  const depositAmountLabel = hasDeposit
    ? formatMoney(billingSummary.depositRequired || billingSummary.depositAmount || record?.depositAmount)
    : 'No deposit required';
  const depositStatusLabel =
    !hasDeposit
      ? 'Not required'
      : depositStatus === 'paid'
      ? 'Received'
      : showDepositAction
      ? 'Payment available'
      : 'Due';
  const depositSummaryNote =
    !hasDeposit
      ? 'No deposit is required for this reservation.'
      : depositStatus === 'paid'
      ? depositPaymentUrl
        ? 'Your deposit has been received and credited to this reservation.'
        : 'Your deposit has been received.'
      : showDepositAction
      ? 'Use the secure payment button below to complete the deposit and keep this reservation moving.'
      : 'The deposit is due. Please contact the transportation team if you need a payment link.';
  const footerAction = showDepositAction
    ? {
        label: 'Pay Deposit',
        eyebrow: 'Deposit due',
        amount: formatMoney(billingSummary.depositRequired || billingSummary.depositAmount || record?.depositAmount),
        href: depositPaymentUrl,
      }
    : showBalanceAction
    ? {
        label: 'Pay Balance',
        eyebrow: 'Balance due',
        amount: formatMoney(balanceRemaining),
        href: balancePaymentUrl,
      }
    : showDepositRecordAction
    ? {
        label: 'View Record',
        eyebrow: 'Deposit received',
        amount: formatMoney(depositPaidAmount),
        href: depositPaymentUrl,
      }
    : null;

  if (isLoading) {
    return (
      <main className="min-h-screen bg-surface">
        <LoadingSpinner className="py-24" />
      </main>
    );
  }

  if (error || !record) {
    return (
      <main className="min-h-screen bg-surface px-4 py-16">
        <div className="mx-auto max-w-xl rounded-card border border-outline/60 bg-surface-container-lowest p-6 text-center">
          <h1 className="text-xl font-semibold text-on-surface">
            Reservation record unavailable
          </h1>
              <p className="mt-2 text-sm leading-6 text-on-surface-variant">
                This record link may have been revoked or replaced. Please contact
            the transportation provider for an updated copy.
              </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-surface pb-28 lg:pb-0">
      <header className="bg-[#101724] text-white">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div className="flex min-w-0 items-center gap-4">
              {company.logoUrl ? (
                <img
                  src={company.logoUrl}
                  alt={companyName}
                  className="h-16 w-auto max-w-[15rem] rounded-md object-contain"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 bg-white/10 text-lg font-semibold">
                  {companyName.slice(0, 1)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-white/70">Reservation record</p>
                <h1 className="text-xl font-semibold leading-tight sm:text-2xl">
                  {companyName}
                </h1>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-sm text-white/70">
                {record.reservationRef || record.id}
              </p>
              <span className="mt-2 inline-flex rounded-pill border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium">
                {statusLabel(record)}
              </span>
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div>
              <p className="text-sm font-medium uppercase text-white/60">
                For your records
              </p>
              <h2 className="mt-2 text-3xl font-semibold">
                {record.customer?.name ||
                  tripDetails.customer_name ||
                  record.customer?.email ||
                  'Guest'}
              </h2>
              {record.customer?.email ? (
                <p className="mt-2 text-white/70">{record.customer.email}</p>
              ) : null}
              <p className="mt-5 max-w-2xl text-base leading-7 text-white/75">
                This is your reservation record. Review the trip time,
                itinerary, vehicle, pricing, and terms below.
              </p>
            </div>
            <div className="rounded-card border border-white/15 bg-white/10 p-4">
              <p className="text-sm text-white/70">Balance remaining</p>
              <p className="mt-1 text-3xl font-semibold">
                {formatMoney(balanceRemaining)}
              </p>
              <p className="mt-3 text-sm text-white/70">
                Reservation total:{' '}
                <span className="font-semibold text-white">
                  {formatMoney(contractTotal)}
                </span>
              </p>
              <p className="mt-1 text-sm text-white/70">
                Deposit amount:{' '}
                <span className="font-semibold text-white">
                  {formatMoney(billingSummary.depositRequired || billingSummary.depositAmount || record.depositAmount)}
                </span>
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="space-y-6">
          <Section
            eyebrow="Trip"
            title={tripDetails.event_name || 'Transportation service'}
            description={`Service is scheduled for ${formatDate(record.startDate)}.`}
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-card bg-surface-container-low p-3">
                <CalendarDaysIcon className="h-5 w-5 text-on-surface-variant" />
                <p className="mt-3 text-xs font-semibold uppercase text-on-surface-variant">
                  Date
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatDate(record.startDate)}
                </p>
              </div>
              <div className="rounded-card bg-surface-container-low p-3">
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  Pickup
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatDateTime(record.startDate)}
                </p>
              </div>
              <div className="rounded-card bg-surface-container-low p-3">
                <p className="text-xs font-semibold uppercase text-on-surface-variant">
                  Dropoff
                </p>
                <p className="mt-1 font-semibold text-on-surface">
                  {formatDateTime(record.endDate)}
                </p>
              </div>
            </div>
          </Section>

          <Section
            eyebrow="Itinerary"
            title="Pickup, Stops, and Dropoff"
            description="These are the locations and notes for your trip."
          >
            <div className="divide-y divide-outline/60">
              {[
                {
                  label: 'Pickup',
                  address: formatAddress(
                    pickupAddress,
                    tripDetails.pickup_location || 'TBD',
                  ),
                  note: getAddressNote(pickupAddress),
                },
                ...stops.map((stop: any, index: number) => ({
                  label: `Stop ${index + 1}`,
                  address: getStopAddress(stop),
                  note: getAddressNote(stop.address || stop),
                })),
                {
                  label: 'Dropoff',
                  address: formatAddress(
                    dropoffAddress,
                    tripDetails.dropoff_location || 'TBD',
                  ),
                  note: getAddressNote(dropoffAddress),
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[120px_minmax(0,1fr)]"
                >
                  <div>
                    <p className="text-sm font-semibold text-on-surface">
                      {item.label}
                    </p>
                    <span
                      className="mt-2 hidden h-2 w-2 rounded-full sm:block"
                      style={{ backgroundColor: primaryColor }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-on-surface">{item.address}</p>
                    {item.note ? (
                      <p className="mt-1 text-sm text-on-surface-variant">
                        {item.note}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section
            eyebrow="Vehicle"
            title="Your Vehicle"
            description="Vehicle details for this reservation."
          >
            <div className="divide-y divide-outline/60">
              {(vehicleRequirements.length
                ? vehicleRequirements
                : [{ id: null, vehicle_label: tripDetails.vehicle_label, quantity: 1 }]
              ).map((requirement: any, index: number) => {
                const assigned = vehicleAssignments.filter(
                  (assignment: any) =>
                    !requirement.id ||
                    assignment.quoteRequirementId === requirement.id,
                );
                return (
                  <div
                    key={requirement.id || `requirement-${index}`}
                    className="py-4 first:pt-0 last:pb-0"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-base font-semibold text-on-surface">
                          {requirement.quantity || 1}{' '}
                          {labelize(
                            requirement.vehicle_label ||
                              requirement.vehicle_type ||
                              tripDetails.vehicle_label ||
                              'Vehicle',
                          )}
                        </p>
                        <p className="mt-1 text-sm text-on-surface-variant">
                          {requirement.passenger_capacity_required
                            ? `Passenger capacity: ${requirement.passenger_capacity_required}`
                            : 'Reserved transportation'}
                        </p>
                      </div>
                      <p className="font-semibold text-on-surface">
                        {formatMoney(requirement.subtotal)}
                      </p>
                    </div>
                    <div className="mt-3 space-y-2">
                      {assigned.length ? (
                        assigned.map((assignment: any) => (
                          <div
                            key={assignment.id}
                            className="rounded-card bg-surface-container-low p-3"
                          >
                            <div className="flex items-start gap-3">
                              <TruckIcon className="mt-0.5 h-5 w-5 text-on-surface-variant" />
                              <div>
                                <p className="text-sm font-semibold text-on-surface">
                                  {[
                                    assignment.vehicle?.fleetNumber,
                                    assignment.vehicle?.make,
                                    assignment.vehicle?.model,
                                  ]
                                    .filter(Boolean)
                                    .join(' ') || 'Assigned vehicle'}
                                </p>
                                <p className="mt-1 text-xs text-on-surface-variant">
                                  {[
                                    assignment.vehicle?.licensePlate,
                                    assignment.vehicle?.vehicleType
                                      ? labelize(assignment.vehicle.vehicleType)
                                      : null,
                                    assignment.vehicle?.passengerCapacity
                                      ? `${assignment.vehicle.passengerCapacity} passengers`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' - ')}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="rounded-card bg-surface-container-low p-3 text-sm text-on-surface-variant">
                          Vehicle details will be shared with you before service.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {termsSnapshot ? (
            <Section
              eyebrow="Terms"
              title="Payment and Cancellation Terms"
              description="The terms below apply to this reservation."
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-card bg-surface-container-low p-3">
                  <p className="text-xs font-semibold uppercase text-on-surface-variant">
                    Deposit
                  </p>
                  <p className="mt-1 font-semibold text-on-surface">
                    {formatDepositPolicy(termsSnapshot)}
                  </p>
                </div>
                <div className="rounded-card bg-surface-container-low p-3">
                  <p className="text-xs font-semibold uppercase text-on-surface-variant">
                    Balance
                  </p>
                  <p className="mt-1 font-semibold text-on-surface">
                    {labelize(termsSnapshot.payment_terms)}
                  </p>
                </div>
              </div>
              {termsSnapshot.terms_text ? (
                <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-on-surface-variant">
                  {termsSnapshot.terms_text}
                </p>
              ) : null}
            </Section>
          ) : null}
        </section>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5 shadow-ambient">
            <h3 className="text-base font-semibold text-on-surface">
              Record Summary
            </h3>
            <dl className="mt-4 space-y-2 text-sm">
              <SummaryLine label="Reservation" value={record.reservationRef || record.id} />
              <SummaryLine
                label="Quote"
                value={quoteSource.number || tripDetails.quote_number || 'N/A'}
              />
              <SummaryLine label="Status" value={statusLabel(record)} />
              {billingSummary.subtotal ? (
                <SummaryLine label="Trip subtotal" value={formatMoney(billingSummary.subtotal)} />
              ) : null}
              {billingSummary.serviceCharge && Number(billingSummary.serviceCharge) > 0 ? (
                <SummaryLine label="Service charge" value={formatMoney(billingSummary.serviceCharge)} />
              ) : null}
              {billingSummary.gratuity && Number(billingSummary.gratuity) > 0 ? (
                <SummaryLine label="Gratuity" value={formatMoney(billingSummary.gratuity)} />
              ) : null}
              {billingSummary.tax && Number(billingSummary.tax) > 0 ? (
                <SummaryLine label="Tax" value={formatMoney(billingSummary.tax)} />
              ) : null}
              <SummaryLine label="Reservation total" value={formatMoney(contractTotal)} />
              <SummaryLine label="Deposit amount" value={depositAmountLabel} />
              <SummaryLine label="Deposit status" value={depositStatusLabel} />
              {depositStatus === 'paid' ? (
                <SummaryLine label="Deposit received" value={formatMoney(depositPaidAmount)} />
              ) : null}
              {billingSummary.balanceInvoiced && Number(billingSummary.balanceInvoiced) > 0 ? (
                <SummaryLine label="Balance invoiced" value={formatMoney(billingSummary.balanceInvoiced)} />
              ) : null}
              {billingSummary.balancePaid && Number(billingSummary.balancePaid) > 0 ? (
                <SummaryLine label="Balance paid" value={formatMoney(billingSummary.balancePaid)} />
              ) : null}
              <SummaryLine label="Balance status" value={balanceStatusLabel} />
              <SummaryLine label="Balance remaining" value={formatMoney(balanceRemaining)} />
            </dl>
            <p className="mt-3 rounded-card bg-surface-container-low p-3 text-xs leading-5 text-on-surface-variant">
              {depositStatus === 'due' ? depositSummaryNote : balanceSummaryNote}
            </p>
            {showDepositAction ? (
              <a
                className="mt-5 block rounded-card px-4 py-3 text-center text-sm font-semibold text-white transition hover:brightness-95"
                href={depositPaymentUrl}
                style={{ backgroundColor: primaryColor }}
              >
                Pay Deposit {formatMoney(billingSummary.depositRequired || billingSummary.depositAmount || record.depositAmount)}
              </a>
            ) : null}
            {showDepositRecordAction ? (
              <a
                className="mt-5 block rounded-card px-4 py-3 text-center text-sm font-semibold text-white transition hover:brightness-95"
                href={depositPaymentUrl}
                style={{ backgroundColor: primaryColor }}
              >
                View Deposit Record
              </a>
            ) : null}
            {showBalanceAction ? (
              <a
                className="mt-3 block rounded-card px-4 py-3 text-center text-sm font-semibold text-white transition hover:brightness-95"
                href={balancePaymentUrl}
                style={{ backgroundColor: primaryColor }}
              >
                Pay Balance {formatMoney(balanceRemaining)}
              </a>
            ) : null}
            <button
              type="button"
              className="btn-secondary mt-3 inline-flex w-full items-center justify-center gap-2"
              onClick={() => window.print()}
            >
              <PrinterIcon className="h-4 w-4" />
              Print Record
            </button>
          </div>

          <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-5">
            <h3 className="text-base font-semibold text-on-surface">
              Questions
            </h3>
            <p className="mt-2 text-sm leading-6 text-on-surface-variant">
              Contact {companyName} if pickup details, passenger
              count, or timing need to be adjusted.
            </p>
            <div className="mt-4 space-y-2 text-sm">
              {company.supportEmail ? (
                <a className="block font-medium underline" href={`mailto:${company.supportEmail}`}>
                  {company.supportEmail}
                </a>
              ) : null}
              {company.phone ? (
                <a className="block font-medium underline" href={`tel:${company.phone}`}>
                  {company.phone}
                </a>
              ) : null}
              {company.website ? (
                <a
                  className="block font-medium underline"
                  href={company.website}
                  target="_blank"
                  rel="noreferrer"
                >
                  Company website
                </a>
              ) : null}
            </div>
          </div>
        </aside>
      </div>

	      {footerAction?.href ? (
	        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-outline/70 bg-surface-container-lowest/95 px-4 py-3 shadow-float backdrop-blur lg:hidden">
	          <div className="mx-auto flex max-w-lg items-center gap-3">
	            <div className="min-w-0 flex-1">
	              <p className="text-xs text-on-surface-variant">
	                {footerAction.eyebrow}
	              </p>
	              <p className="font-semibold text-on-surface">
	                {footerAction.amount}
	              </p>
	            </div>
	            <a
	              className="rounded-card px-4 py-3 text-sm font-semibold text-white transition hover:brightness-95"
	              href={footerAction.href}
	              style={{ backgroundColor: primaryColor }}
	            >
	              {footerAction.label}
	            </a>
	          </div>
	        </div>
      ) : null}
    </main>
  );
}
